import type { Permission } from '../api/types';

export interface NavContext {
  can: (permission: Permission) => boolean;
  // fieldOnly: reiner Außendienst (Makler) – mobile, fokussierte Sicht ohne Bürofunktionen.
  fieldOnly: boolean;
  // orderPrice: Partner im Auftragspreis (Konzept 10) – sieht seine Preisliste.
  orderPrice?: boolean;
  // telesales: Rolle Terminierer (Telefonakquise) – nur sie arbeitet in der Akquise; Support telefoniert aus dem Termin bzw.
  // der Rubrik „Terminbestätigungen“ (OFFENE_FRAGEN B-29).
  telesales?: boolean;
  // supportOnly / telesalesOnly: reiner Support bzw. reine Terminplanung – Menü nur mit ihrer Arbeit (Fokus statt Rechte, B-38).
  supportOnly?: boolean;
  telesalesOnly?: boolean;
}

export interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  visible: (c: NavContext) => boolean;
}

// Büro mit Klärungsaufgaben (Support, Leitung). Reine Telefonisten arbeiten in der Akquise; ihre Rückrufe und
// Wiedervorlagen liegen dort, Rückfragen hängen am Lead.
export const isBackOffice = (c: NavContext) => !c.fieldOnly && (c.can('ticket.manage') || c.can('lead.geo_review'));

// Navigation nach Arbeitsbereichen statt nach Funktionen: Aufgaben, Termine und Leads sind jeweils ein Bereich
// mit Reitern; Gebiete und Makler sind die Steuerungsebene des Projekts. Profil liegt im Benutzermenü.
export const navItems: NavItem[] = [
  { to: '.', label: 'Heute', end: true, visible: () => true },
  // Makler (OFFENE_FRAGEN B-28): eigene Termine (Tag, Woche, Monat) und eigene Leads – Fokus, keine Bürofunktionen.
  { to: 'termine', label: 'Termine', visible: (c) => c.fieldOnly && c.can('appointment.read') },
  { to: 'leads', label: 'Meine Leads', visible: (c) => c.fieldOnly && c.can('lead.read') },
  { to: 'akquise', label: 'Akquise', visible: (c) => c.can('call.start') && (c.telesales !== false || c.can('project.manage')) },
  { to: 'posteingang', label: 'Posteingang', visible: (c) => isBackOffice(c) && !c.supportOnly },
  { to: 'posteingang', label: 'Meine Aufgaben', visible: (c) => isBackOffice(c) && Boolean(c.supportOnly) },
  { to: 'leads', label: 'Leads', visible: (c) => !c.fieldOnly && c.can('lead.read') },
  // Kaufgesuche: Eigentümer, die selbst suchen – im Menü der Leitung; der Makler hat sie unter „Meine Leads“.
  { to: 'kaufgesuche', label: 'Kaufgesuche', visible: (c) => c.can('lead.read') && !c.fieldOnly && !c.supportOnly && !c.telesalesOnly },
  {
    to: 'termine',
    label: 'Termine',
    visible: (c) => !c.fieldOnly && !c.supportOnly && (isBackOffice(c) || c.can('appointment.reschedule')) && c.can('calendar.read'),
  },
  // Partner-Pipeline (Dominik 23./26.10.2026): Neukunden-Akquise Maklerpass-Partner – Leitung und Terminplanung (Thomas).
  { to: 'partner', label: 'Partner', visible: (c) => !c.fieldOnly && !c.supportOnly && (c.can('project.manage') || (c.can('area.read') && c.can('call.start'))) },
  { to: 'gebiete', label: 'Gebiete & Makler', visible: (c) => isBackOffice(c) && !c.supportOnly && c.can('area.read') },
  // Auswertung der Gespräche (Einwände, Anliegen, Monatsbericht) für die Führung.
  { to: 'gespraeche', label: 'Gespräche', visible: (c) => !c.fieldOnly && c.can('call.review') },
  { to: 'tickets', label: 'Rückfragen', end: true, visible: (c) => (c.fieldOnly || c.can('appointment.reschedule')) && !isBackOffice(c) && c.can('ticket.read') },
  // Vertriebsstruktur und Provisionen (globale Vertriebsrollen; projektübergreifend).
  // Preisliste Auftragspreis (OFFENE_FRAGEN B-22): Leitung und Partner im Auftragspreis (Support erreicht sie über die Makler-Seite).
  { to: 'preisliste', label: 'Preisliste', visible: (c) => (!c.fieldOnly && c.can('project.manage')) || Boolean(c.orderPrice) },
  // Rechnungsstellung (OFFENE_FRAGEN B-22): nur die Leitung.
  { to: 'rechnungen', label: 'Rechnungen', visible: (c) => !c.fieldOnly && c.can('project.manage') },
  { to: 'vertrieb', label: 'Vertrieb', visible: (c) => c.can('sales.read_own') || c.can('sales.manage') },
  { to: 'verwaltung', label: 'Verwaltung', visible: (c) => c.can('project.manage') || c.can('user.manage') || c.can('system.manage') },
];
